import { Action } from 'store/store';
import { WidgetSetConfigPayload } from 'widgets/store/configuration/actions';
import { createGridColumnSortReducer } from 'widgets/store/grid/sorting/reducer';
import { ToggleGridColumnSortAction } from 'widgets/store/grid/sorting/actions';
import { createColumnWidthsReducer } from 'widgets/store/grid/columnWidth/reducer';
import { SetGridColumnWidthAction } from 'widgets/store/grid/columnWidth/actions';
import { WidgetType } from 'widgets/WidgetType';
import { GridSortState } from 'widgets/store/grid/sorting/GridSortState';
import { GridColumnWidthState } from 'widgets/store/grid/columnWidth/GridColumnWidthState';
import { GridVisibleColumnState } from 'widgets/store/grid/visibleColumns/GridVisibleColumnState';
import { createConfigurationReducer } from 'widgets/store/configuration/reducer';
import { Reducer } from 'redux';

export type ConfigStateWithGridSupport<TColumnId extends string> =
    GridSortState<TColumnId> &
        GridColumnWidthState<TColumnId> &
        GridVisibleColumnState<TColumnId>;

export type ConfigStateWithGridSupportGenericAction<
    TColumnId extends string,
    TConfigState extends ConfigStateWithGridSupport<TColumnId>,
> = Action<string, Partial<WidgetSetConfigPayload<TConfigState>>>;

export type ConfigStateWithGridSupportSupportedActions<
    TColumnId extends string,
    TConfigState extends ConfigStateWithGridSupport<TColumnId>,
> =
    | ConfigStateWithGridSupportGenericAction<TColumnId, TConfigState>
    | ToggleGridColumnSortAction<TColumnId>
    | SetGridColumnWidthAction<TColumnId>;

export interface BuildConfigReducerWithGridSupportOptions<
    TColumnId extends string,
    TConfigState extends ConfigStateWithGridSupport<TColumnId>,
> {
    preProcessor?: (
        action: ConfigStateWithGridSupportSupportedActions<
            TColumnId,
            TConfigState
        >
    ) => void;
    /*
     * If supplied, the postReducer will be run after the generic config reducer...
     */
    postReducer?: Reducer<
        TConfigState,
        Action<string, Partial<WidgetSetConfigPayload<TConfigState>>>
    >;
}

export const buildConfigReducerWithGridSupport = <
    TColumnId extends string,
    TConfigState extends ConfigStateWithGridSupport<TColumnId>,
>(
    widgetType: WidgetType,
    initialState: TConfigState,
    options?: BuildConfigReducerWithGridSupportOptions<TColumnId, TConfigState>
) => {
    const genericConfigReducer = createConfigurationReducer(
        initialState,
        options?.postReducer
    );

    const columnSortReducer = createGridColumnSortReducer(
        widgetType,
        initialState
    );

    const columnWidthsReducer = createColumnWidthsReducer<
        TColumnId,
        TConfigState
    >(widgetType, initialState);

    return (
        state = initialState,
        action: ConfigStateWithGridSupportSupportedActions<
            TColumnId,
            TConfigState
        >
    ) => {
        if (widgetType !== action.payload?.widgetType) {
            return state;
        }

        if (options?.preProcessor) {
            options.preProcessor(action);
        }

        let nextState = columnSortReducer(
            state,
            action as ToggleGridColumnSortAction<TColumnId>
        );
        nextState = columnWidthsReducer(
            nextState,
            action as SetGridColumnWidthAction<TColumnId>
        );
        return genericConfigReducer(
            nextState,
            action as ConfigStateWithGridSupportGenericAction<
                TColumnId,
                TConfigState
            >
        );
    };
};
